import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTvOutlined";
import MovieIcon from "@mui/icons-material/MovieCreationOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import FolderIcon from "@mui/icons-material/PermMediaOutlined";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  Asset,
  AssetType,
  Genre,
  Provider,
} from "@shared/types/content-api";
import {
  assetToDraft,
  draftAsAsset,
  draftRefusals,
  emptiedCollections,
  emptyAssetDraft,
  type AssetDraft,
} from "@shared/asset-draft";
import { assetPlaybackBlockers } from "@shared/channel-playability";
import { availabilityFor, rightsIssues } from "@shared/content-rights";
import { imageIssues } from "@shared/vod-images";
import {
  localisedIssues,
  localisedStatus,
  localisedSummary,
} from "@shared/vod-localised";
import {
  VOD_ASSET_TYPES,
  durationIssues,
  genreDisplayName,
  yearIssues,
} from "@shared/vod-title";
import { tokens } from "../../theme";
import { ItemFormSurface, type Dismiss } from "../EditDrawer";
import AvailabilityChip from "./AvailabilityChip";
import AvailabilityEditor from "./AvailabilityEditor";
import ChannelEpgMapping from "./ChannelEpgMapping";
import ChannelSchedulePreview from "./ChannelSchedulePreview";
import MediaFilesPanel from "./MediaFilesPanel";
import LocalisedContentPanel from "./LocalisedContentPanel";
import LocalisedImagesPanel from "./LocalisedImagesPanel";
import MediasEditor from "./MediasEditor";
import {
  Field,
  SectionHead,
  SoftChip,
  StatusDot,
  StringListField,
} from "./editorBits";
import {
  blockerText,
  durationIssueText,
  imageIssueText,
  localisedIssueText,
  rightsIssueText,
  saveErrorText,
  yearIssueText,
} from "./findings";
import TitleOwnerSection from "./TitleOwnerSection";
import { PermissionGate } from "../../auth/access";
import { AUTHOR_CHANNELS, AUTHOR_TITLES } from "./catalogueGates";

/**
 * The Distribution editor - one dialog, two bodies (`BE-15`).
 *
 * ## Why one component and not two
 *
 * A channel and a VOD title are the **same row upstream** - `CmsAsset`, with
 * `type: "channel"` or `type: "movie"`. The server learned this the expensive
 * way and put its media readers, refusals, wipe guard and provider check in one
 * shared boundary (`contentAssetBody.ts`), because two copies would mean one
 * half of the catalogue silently accepting what the other half refuses. This is
 * that argument one layer up: two look-alike dialogs over one row is how the
 * channel editor ends up able to express something the title editor cannot.
 *
 * What genuinely differs is the middle: a channel is a feed you carry
 * (distributor metadata plus the media a player tunes), a title is a work you
 * shelve (year, length, studio, and its place in a series). Everything around
 * that middle - localised text, artwork, media, licence windows, the derived
 * verdicts - is identical, and identical is how it is written here.
 *
 * ## Seeded once, remounted by key
 *
 * ```tsx
 * <AssetEditorDialog key={editing?.assetId ?? "new"} initial={editing} … />
 * ```
 *
 * The caller changes the key; the dialog seeds itself once and never watches
 * props. A prop-watching reset cannot tell *"opened a different record"* from
 * *"reopened the same one"*, so it either keeps stale state or discards live
 * edits depending on which row you picked - and both are invisible until
 * somebody saves.
 *
 * ⚠️ This matters **more** now that the initial value is fetched, not less. An
 * async load means props arrive after the first render, which is exactly the
 * moment a "did it change?" check has to guess.
 *
 * ## Every verdict on screen is DERIVED
 *
 * The rail draws `assetPlaybackBlockers`, `availabilityFor`, `imageIssues`,
 * `localisedIssues`, `yearIssues` and `durationIssues` over the draft in hand -
 * the same functions the list and the server call, over the same shapes. None
 * of it is stored, and none of it is recomputed by a second rule that would
 * start agreeing by luck. That is why a half-typed form is a legitimate
 * argument to all six.
 */

export type AssetEditorKind = "channel" | "title";

export interface AssetEditorDialogProps {
  open: boolean;
  kind: AssetEditorKind;
  /** The row being edited, or null to create. Whole assets only - a row IS its draft. */
  initial: Asset | null;
  providers: Provider[];
  genres: Genre[];
  saving?: boolean;
  /** The server's refusal, as it came back. */
  error?: { code?: string; message: string } | null;
  onClose: () => void;
  onSubmit: (
    draft: AssetDraft,
    options: { confirmEmptyCollections: boolean }
    /** Resolves true when saved: the surface then closes itself (onClose). */
  ) => Promise<boolean>;
}

function ManageLink({
  onClick,
  testId,
}: {
  onClick: () => void;
  testId: string;
}) {
  const { t } = useTranslation("content");
  return (
    <Button
      size="small"
      onClick={onClick}
      data-testid={testId}
      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
      sx={{
        minHeight: 0,
        px: 0,
        fontSize: 14,
        fontWeight: "fontWeightSemibold",
      }}
    >
      {t("assetEditorDialog.manage")}
    </Button>
  );
}

const BULLET = "• ";

/** One derived finding, drawn the same way wherever it came from. */
function Finding({ text, tone }: { text: string; tone: "blocker" | "issue" }) {
  return (
    <Typography
      variant="meta"
      sx={{
        color: tone === "blocker" ? tokens.error : tokens.inkMuted,
        lineHeight: 1.5,
      }}
    >
      {BULLET}
      {text}
    </Typography>
  );
}

export default function AssetEditorDialog({
  open,
  kind,
  initial,
  providers,
  genres,
  saving = false,
  error = null,
  onClose,
  onSubmit,
}: AssetEditorDialogProps) {
  const { t } = useTranslation(["content", "common"]);
  const [draft, setDraft] = useState<AssetDraft>(() =>
    initial
      ? assetToDraft(initial)
      : emptyAssetDraft(kind === "channel" ? "channel" : "movie")
  );
  const [contentPanel, setContentPanel] = useState(false);
  const [imagesPanel, setImagesPanel] = useState(false);
  const [mediaFiles, setMediaFiles] = useState(false);
  /** Set once the operator has been shown, and accepted, what a save will delete. */
  const [wipeAccepted, setWipeAccepted] = useState(false);

  const isEdit = initial !== null;
  const set = <K extends keyof AssetDraft>(key: K, next: AssetDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: next }));

  const derived = useMemo(() => draftAsAsset(draft, initial), [draft, initial]);
  const blockers = useMemo(
    () =>
      assetPlaybackBlockers(
        derived,
        kind === "channel" ? ["channel", "main"] : ["main"]
      ),
    [derived, kind]
  );
  const availability = useMemo(() => availabilityFor(derived), [derived]);
  const refusals = draftRefusals(draft);
  /**
   * Which collections this save would EMPTY.
   *
   * Named before the operator is asked rather than after the server refuses:
   * upstream a restated `[]` DELETES every row in that collection, and "the
   * panel failed to load" and "I meant to remove all of them" arrive on the
   * wire as the same payload.
   */
  const wiping = useMemo(
    () => (initial ? emptiedCollections(draft, initial) : []),
    [draft, initial]
  );

  const localisedRows = draft.localizedInfo;
  const doneLanguages = localisedRows.filter(
    (row) => localisedStatus(row) === "done"
  ).length;

  const seriesFields = kind === "title" && draft.type !== "movie";

  async function submit(dismiss: Dismiss) {
    if (wiping.length && !wipeAccepted) {
      setWipeAccepted(true);
      return;
    }
    const ok = await onSubmit(draft, {
      confirmEmptyCollections: wiping.length > 0,
    });
    // The page saved; slide away first, then the page unmounts (onClose).
    if (ok) dismiss();
  }

  return (
    <ItemFormSurface
      mode={isEdit ? "edit" : "create"}
      open={open}
      onClose={onClose}
      busy={saving}
      maxWidth="xl"
      drawerWidth={1240}
      data-testid={`edit-${kind}-dialog`}
      icon={kind === "channel" ? <LiveTvIcon /> : <MovieIcon />}
      title={
        isEdit
          ? kind === "channel"
            ? t("assetEditorDialog.title.editChannel")
            : t("assetEditorDialog.title.editTitle")
          : kind === "channel"
            ? t("assetEditorDialog.title.createChannel")
            : t("assetEditorDialog.title.createNewTitle")
      }
      subtitle={
        kind === "channel"
          ? t("assetEditorDialog.subtitle.channel")
          : t("assetEditorDialog.subtitle.title")
      }
      headerAside={
        isEdit &&
        initial && (
          <Stack spacing={0.5} alignItems="flex-end" sx={{ pt: 0.5 }}>
            <AvailabilityChip availability={availability} />
            <Typography
              variant="micro"
              sx={{ color: tokens.inkMuted, fontFamily: "fontFamilyMono" }}
            >
              {initial.assetId}
            </Typography>
          </Stack>
        )
      }
      footerTop={
        <>
          {refusals.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {
                refusals.map((r) => {
                  if (r.field === "externalId")
                    return t("assetEditorDialog.refusal.externalId");
                  if (r.field === "originalTitle")
                    return t("assetEditorDialog.refusal.originalTitle");
                  if (r.field === "resourceUrl")
                    return t("assetEditorDialog.refusal.resourceUrl", {
                      index: r.mediaIndex + 1,
                    });
                  return t("assetEditorDialog.refusal.imageLanguage", {
                    index: r.index + 1,
                  });
                })[0]
              }
            </Alert>
          )}
          {wiping.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="wipe-warning">
              {t("assetEditorDialog.wipe", { collections: wiping.join(", ") })}{" "}
              {wipeAccepted
                ? t("assetEditorDialog.wipeConfirm")
                : t("assetEditorDialog.wipeAsk")}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="editor-error">
              {saveErrorText(t, error.code, error.message)}
            </Alert>
          )}
        </>
      }
      actions={(dismiss) => (
        <>
          <Button
            variant="outlined"
            onClick={() => dismiss()}
            disabled={saving}
            sx={{ width: 124 }}
          >
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            {...(kind === "channel" ? AUTHOR_CHANNELS : AUTHOR_TITLES)}
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              onClick={() => void submit(dismiss)}
              disabled={saving || refusals.length > 0}
              sx={{ minWidth: 155 }}
              data-testid="asset-submit"
            >
              {saving
                ? t("assetEditorDialog.saving")
                : isEdit
                  ? t("assetEditorDialog.saveChanges")
                  : kind === "channel"
                    ? t("assetEditorDialog.createChannel")
                    : t("assetEditorDialog.createTitle")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={6}>
        <Stack spacing={6} sx={{ flex: "1 1 auto", minWidth: 0 }}>
          {/* ---- Basics ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.basics.title")}
              subtitle={
                kind === "channel"
                  ? t("assetEditorDialog.basics.channelSubtitle")
                  : t("assetEditorDialog.basics.titleSubtitle")
              }
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 3.5,
              }}
            >
              <Field
                label={t("common:labels.name")}
                placeholder={t("assetEditorDialog.basics.namePlaceholder")}
                value={draft.originalTitle}
                onChange={(e) => set("originalTitle", e.target.value)}
                inputProps={{ "data-testid": "asset-name" }}
                required
              />
              <Field
                label={t("assetEditorDialog.basics.distributorId")}
                placeholder={t(
                  "assetEditorDialog.basics.distributorIdPlaceholder"
                )}
                value={draft.externalId}
                onChange={(e) => set("externalId", e.target.value)}
                inputProps={{ "data-testid": "asset-external-id" }}
                helperText={t("assetEditorDialog.basics.distributorIdHint")}
                required
              />
              <Field
                select
                label={t("assetEditorDialog.basics.provider")}
                value={draft.providerId}
                onChange={(e) => set("providerId", e.target.value)}
                SelectProps={{ displayEmpty: true }}
                inputProps={{ "data-testid": "asset-provider" }}
              >
                <MenuItem value="">
                  <em>{t("assetEditorDialog.basics.noProvider")}</em>
                </MenuItem>
                {providers.map((p) => (
                  <MenuItem key={p.providerId} value={p.providerId}>
                    {p.name || p.providerId}
                  </MenuItem>
                ))}
              </Field>

              {kind === "title" && (
                <Field
                  select
                  label={t("common:labels.type")}
                  value={draft.type}
                  disabled={isEdit}
                  onChange={(e) => set("type", e.target.value as AssetType)}
                  helperText={
                    isEdit
                      ? t("assetEditorDialog.basics.typeFixed")
                      : t("assetEditorDialog.basics.typeHint")
                  }
                >
                  {VOD_ASSET_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`assetEditorDialog.assetType.${type}`)}
                    </MenuItem>
                  ))}
                </Field>
              )}

              <Field
                label={t("assetEditorDialog.basics.originalLanguage")}
                placeholder="sv"
                value={draft.originalLanguage}
                onChange={(e) => set("originalLanguage", e.target.value)}
              />
              <Field
                label={t("assetEditorDialog.basics.countryOfOrigin")}
                placeholder="se"
                value={draft.countryOfOrigin}
                onChange={(e) => set("countryOfOrigin", e.target.value)}
              />

              {kind === "title" && (
                <>
                  <Field
                    label={t("assetEditorDialog.basics.productionYear")}
                    placeholder={t(
                      "assetEditorDialog.basics.productionYearPlaceholder"
                    )}
                    value={draft.productionYear}
                    onChange={(e) => set("productionYear", e.target.value)}
                    helperText={t(
                      "assetEditorDialog.basics.productionYearHint"
                    )}
                  />
                  <Field
                    label={t("assetEditorDialog.basics.length")}
                    placeholder={t(
                      "assetEditorDialog.basics.lengthPlaceholder"
                    )}
                    value={draft.duration}
                    onChange={(e) => set("duration", e.target.value)}
                    helperText={t("assetEditorDialog.basics.lengthHint")}
                    inputProps={{ "data-testid": "asset-duration" }}
                  />
                  <Field
                    label={t("assetEditorDialog.basics.studio")}
                    value={draft.studio}
                    onChange={(e) => set("studio", e.target.value)}
                  />
                </>
              )}

              <Field
                label={t("assetEditorDialog.basics.copyright")}
                value={draft.copyrightInfo}
                onChange={(e) => set("copyrightInfo", e.target.value)}
              />
              <Field
                label={t("assetEditorDialog.basics.eidrId")}
                value={draft.eidrId}
                onChange={(e) => set("eidrId", e.target.value)}
              />
            </Box>
          </Box>

          {/* ---- Localised content ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.localised.title")}
              subtitle={t("assetEditorDialog.localised.subtitle")}
              aside={
                <SoftChip
                  label={t("assetEditorDialog.localised.complete", {
                    done: doneLanguages,
                    total: localisedRows.length,
                  })}
                />
              }
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                columnGap: 3.5,
                rowGap: 1,
              }}
            >
              {localisedRows.length === 0 && (
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("assetEditorDialog.localised.none")}
                </Typography>
              )}
              {localisedRows.map((row) => (
                <Stack
                  key={row.language}
                  direction="row"
                  alignItems="center"
                  spacing={4}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <StatusDot status={localisedStatus(row)} />
                    <Typography
                      variant="meta"
                      sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
                    >
                      {row.language.toUpperCase()}
                    </Typography>
                  </Stack>
                  <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                    {localisedSummary(row)}
                  </Typography>
                </Stack>
              ))}
            </Box>
            <Stack direction="row" justifyContent="flex-end">
              <ManageLink
                onClick={() => setContentPanel(true)}
                testId="manage-content"
              />
            </Stack>
          </Box>

          {/* ---- Keywords & genres ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.keywords.title")}
              subtitle={t("assetEditorDialog.keywords.subtitle")}
              aside={
                <>
                  <SoftChip
                    label={t("assetEditorDialog.keywords.genreCount", {
                      count: draft.genreIds.length,
                    })}
                  />
                  <SoftChip
                    label={t("assetEditorDialog.keywords.keywordCount", {
                      count: draft.keywords.length,
                    })}
                  />
                </>
              }
            />
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="meta"
                  sx={{
                    fontWeight: "fontWeightBold",
                    color: tokens.ink,
                    mb: 1,
                  }}
                >
                  {t("assetEditorDialog.keywords.genres")}
                </Typography>
                <Field
                  select
                  fullWidth
                  label=""
                  value={draft.genreIds}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {(selected as string[]).map((id) => (
                          <Chip
                            key={id}
                            size="small"
                            label={genreDisplayName(
                              genres.find((g) => g.genreId === id) ?? {
                                genreId: id,
                                name: id,
                                localizedName: [],
                              }
                            )}
                          />
                        ))}
                      </Stack>
                    ),
                  }}
                  onChange={(e) =>
                    set(
                      "genreIds",
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : (e.target.value as unknown as string[])
                    )
                  }
                  inputProps={{ "data-testid": "asset-genres" }}
                >
                  {genres.map((genre) => (
                    <MenuItem key={genre.genreId} value={genre.genreId}>
                      {genreDisplayName(genre)}
                    </MenuItem>
                  ))}
                </Field>
                <Typography
                  variant="micro"
                  sx={{ color: tokens.inkMuted, mt: 0.5 }}
                >
                  {t("assetEditorDialog.keywords.genresHint")}
                </Typography>
              </Box>
              <StringListField
                label={t("assetEditorDialog.keywords.keywords")}
                hint={t("assetEditorDialog.keywords.keywordsHint")}
                value={draft.keywords}
                placeholder={t(
                  "assetEditorDialog.keywords.keywordsPlaceholder"
                )}
                onChange={(next) => set("keywords", next)}
                testId="asset-keyword"
              />
            </Stack>
          </Box>

          {/* ---- Series information ---- */}
          {seriesFields && (
            <Box>
              <SectionHead
                title={t("assetEditorDialog.series.title")}
                subtitle={t("assetEditorDialog.series.subtitle")}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr 1fr 140px 140px",
                  },
                  gap: 3.5,
                }}
              >
                <Field
                  label={t("assetEditorDialog.series.seriesId")}
                  placeholder={t(
                    "assetEditorDialog.series.seriesIdPlaceholder"
                  )}
                  value={draft.seriesId}
                  onChange={(e) => set("seriesId", e.target.value)}
                  disabled={draft.type === "series"}
                  helperText={
                    draft.type === "series"
                      ? t("assetEditorDialog.series.seriesIsParent")
                      : undefined
                  }
                />
                <Field
                  label={t("assetEditorDialog.series.seasonId")}
                  placeholder={t(
                    "assetEditorDialog.series.seasonIdPlaceholder"
                  )}
                  value={draft.seasonId}
                  onChange={(e) => set("seasonId", e.target.value)}
                  disabled={draft.type !== "episode"}
                />
                <Field
                  label={t("assetEditorDialog.series.seasonNo")}
                  value={draft.seasonNumber}
                  onChange={(e) => set("seasonNumber", e.target.value)}
                  disabled={draft.type === "series"}
                />
                <Field
                  label={t("assetEditorDialog.series.episodeNo")}
                  value={draft.episodeNumber}
                  onChange={(e) => set("episodeNumber", e.target.value)}
                  disabled={draft.type !== "episode"}
                />
              </Box>
            </Box>
          )}

          {/* ---- Artwork ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.images.title")}
              subtitle={t("assetEditorDialog.images.subtitle")}
              aside={
                <SoftChip
                  label={t("assetEditorDialog.images.count", {
                    count: draft.images.length,
                  })}
                />
              }
            />
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {draft.images.length === 0 && (
                <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                  {t("assetEditorDialog.images.none")}
                </Typography>
              )}
              {[...new Set(draft.images.map((i) => i.language))].map(
                (language) => (
                  <SoftChip
                    key={language}
                    label={t("assetEditorDialog.images.perLanguage", {
                      language: language.toUpperCase(),
                      count: draft.images.filter((i) => i.language === language)
                        .length,
                    })}
                  />
                )
              )}
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
              <ManageLink
                onClick={() => setImagesPanel(true)}
                testId="manage-images"
              />
            </Stack>
          </Box>

          {/* ---- Media ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.media.title")}
              subtitle={t("assetEditorDialog.media.subtitle")}
              aside={
                <Stack direction="row" spacing={1} alignItems="center">
                  <SoftChip
                    label={t("assetEditorDialog.media.count", {
                      count: draft.medias.length,
                    })}
                  />
                  {/* The way IN to ingest, and it only exists on a saved
                        asset: a job is keyed on assetId, and an asset being
                        created does not have one yet. */}
                  {initial && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FolderIcon />}
                      onClick={() => setMediaFiles(true)}
                      data-testid="open-media-files"
                    >
                      {t("assetEditorDialog.media.mediaFiles")}
                    </Button>
                  )}
                </Stack>
              }
            />
            <MediasEditor
              medias={draft.medias}
              defaultType={kind === "channel" ? "channel" : "main"}
              onChange={(next) => set("medias", next)}
            />
          </Box>

          {/* ---- Programme guide (BE-27) ----
                Read-only, and deliberately HERE rather than on a page of its
                own: nobody authors EPG, so the only question a CMS schedule
                surface answers is "did this channel's mapping produce a
                schedule, and does it look right?" - which belongs beside the
                mapping that decides it (`BE-22` owns that field). A channel
                being created has no assetId and nothing to read yet. */}
          {/* CONTENT-28: which studio's portal shows this title. */}
          {kind === "title" && initial && (
            <TitleOwnerSection assetId={initial.assetId} />
          )}
          {kind === "channel" && initial && (
            <>
              {/* The mapping first, then what it produced (`BE-22` above
                    `BE-27`). An empty schedule is meaningless without the field
                    that decides it: the commonest reason a guide is blank is
                    that nothing was ever mapped, and nothing said so. */}
              <ChannelEpgMapping
                assetId={initial.assetId}
                alternativeIds={initial.alternativeIds}
              />
              <ChannelSchedulePreview assetId={initial.assetId} />
            </>
          )}

          {/* ---- Availability ---- */}
          <Box>
            <SectionHead
              title={t("assetEditorDialog.availability.title")}
              subtitle={t("assetEditorDialog.availability.subtitle")}
              aside={<AvailabilityChip availability={availability} />}
            />
            <AvailabilityEditor
              rows={draft.contentRights}
              onChange={(next) => set("contentRights", next)}
            />
          </Box>
        </Stack>

        {/* ---- The rail: everything derived, nothing stored ---- */}
        <Box sx={{ flex: "0 0 auto", width: { xs: "100%", lg: 359 } }}>
          <Typography
            sx={{
              typography: "titleLg",
              fontWeight: "fontWeightSemibold",
              color: tokens.ink,
              mb: 0.75,
            }}
          >
            {t("assetEditorDialog.summary.title")}
          </Typography>
          <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 2.5 }}>
            {t("assetEditorDialog.summary.intro")}
          </Typography>

          <Stack spacing={2.5}>
            <Box>
              <Typography
                variant="meta"
                sx={{ fontWeight: "fontWeightBold", color: tokens.ink, mb: 1 }}
              >
                {t("assetEditorDialog.summary.fit")}
              </Typography>
              {blockers.length === 0 ? (
                <Typography variant="meta" sx={{ color: tokens.success }}>
                  {t("assetEditorDialog.summary.nothingBlocking")}
                </Typography>
              ) : (
                <Stack spacing={0.5} data-testid="editor-blockers">
                  {blockers.map((blocker, i) => (
                    <Finding
                      key={`${blocker.code}-${i}`}
                      tone="blocker"
                      text={blockerText(t, blocker)}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography
                variant="meta"
                sx={{ fontWeight: "fontWeightBold", color: tokens.ink, mb: 1 }}
              >
                {t("assetEditorDialog.summary.worthKnowing")}
              </Typography>
              <Stack spacing={0.5} data-testid="editor-issues">
                {rightsIssues(derived).map((issue, i) => (
                  <Finding
                    key={`r${i}`}
                    tone="issue"
                    text={rightsIssueText(t, issue)}
                  />
                ))}
                {localisedIssues(derived).map((issue, i) => (
                  <Finding
                    key={`l${i}`}
                    tone="issue"
                    text={localisedIssueText(t, issue)}
                  />
                ))}
                {imageIssues(derived).map((issue, i) => (
                  <Finding
                    key={`i${i}`}
                    tone="issue"
                    text={imageIssueText(t, issue)}
                  />
                ))}
                {kind === "title" &&
                  yearIssues(derived).map((issue, i) => (
                    <Finding
                      key={`y${i}`}
                      tone="issue"
                      text={yearIssueText(t, issue)}
                    />
                  ))}
                {kind === "title" &&
                  durationIssues(derived).map((issue, i) => (
                    <Finding
                      key={`d${i}`}
                      tone="issue"
                      text={durationIssueText(t, issue)}
                    />
                  ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>

      <LocalisedContentPanel
        open={contentPanel}
        rows={draft.localizedInfo}
        onClose={() => setContentPanel(false)}
        onSave={(next) => {
          set("localizedInfo", next);
          setContentPanel(false);
        }}
      />

      {/**
       * ⚠️ `assetId` is "" while the asset is being CREATED, which disables
       * uploading inside the panel rather than breaking it: bytes are filed
       * against a title, and there is no title yet. The row list is still
       * editable, so nothing else about the panel changes — the same reason the
       * ingest block below only renders over a saved asset.
       */}
      <LocalisedImagesPanel
        open={imagesPanel}
        images={draft.images}
        assetId={initial?.assetId ?? ""}
        surface="staff"
        onClose={() => setImagesPanel(false)}
        onSave={(next) => {
          set("images", next);
          setImagesPanel(false);
        }}
        onUploaded={(saved) => set("images", assetToDraft(saved).images)}
      />

      {/* Only over a SAVED asset: an ingest is keyed on assetId and one being
          created has none. Mounted here beside the other panels, but it edits
          nothing in the draft - an ingest is a process, not a field, so it
          neither reads nor writes the form. */}
      {initial && (
        <MediaFilesPanel
          open={mediaFiles}
          assetId={initial.assetId}
          assetTitle={initial.originalTitle || initial.assetId}
          // The SAME derivations the rail draws, handed over rather than
          // recomputed (`BE-32`). Recomputing from the same draft agrees today
          // and is exactly the seam along which two screens drift.
          asset={derived}
          blockers={blockers}
          availability={availability}
          onClose={() => setMediaFiles(false)}
        />
      )}
    </ItemFormSurface>
  );
}
